import Loading from "../../../Layouts/Loading";
import ErrorPage from "../../../Layouts/Error";
import { usePerformanceSummaryQuery } from "../../../Redux/Apis/userExamApi";

type PerformanceSummaryProps = {
  userExamId: number;
  close: (value: boolean) => void;
};

function PerformanceSummary({ userExamId, close }: PerformanceSummaryProps) {
  const { data, isLoading, isSuccess, isError, error } = usePerformanceSummaryQuery(
    +userExamId,
    { refetchOnMountOrArgChange: true }
  );

  const renderGeneralScoreCard = (label: string, score?: number, percentile?: number) => (
    <div
      key={label}
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "flex-start",
        border: "1px solid #c77c50",
        boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
        borderRadius: 0,
        overflow: "hidden",
        minWidth: "220px",
      }}
      className="me-3 mb-2"
    >
      <div
        style={{
          padding: "6px 12px",
          backgroundColor: "#f7ecee",
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          padding: "8px 14px",
          backgroundColor: "#e38b1a",
          color: "#fff",
          fontSize: "18px",
          fontWeight: 700,
          textAlign: "center",
          width: "100%",
        }}
      >
        {typeof score === "number" ? score : "—"}
      </div>
      {typeof percentile === "number" && (
        <div
          style={{
            padding: "4px 12px",
            backgroundColor: "#fdf2de",
            fontSize: "11px",
          }}
        >
          Percentile: {percentile}
        </div>
      )}
    </div>
  );

  const renderSingleScoreCard = (label: string, correct?: number, all?: number) => (
    <div
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "flex-start",
        border: "1px solid #c77c50",
        boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
        borderRadius: 0,
        overflow: "hidden",
        minWidth: "260px",
      }}
      className="me-3 mb-2"
    >
      <div
        style={{
          padding: "6px 12px",
          backgroundColor: "#f7ecee",
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          padding: "8px 14px",
          backgroundColor: "#e38b1a",
          color: "#fff",
          fontSize: "16px",
          fontWeight: 700,
          textAlign: "center",
          width: "100%",
        }}
      >
        {typeof correct === "number" && typeof all === "number"
          ? `${correct} of ${all} Correct`
          : "—"}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div
        dir="ltr"
        className="position-fixed border border-danger-subtle border-3 pt-2 rounded bg-white shadow"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90vh",
          width: "min(960px, 95vw)",
          overflowY: "auto",
        }}
      >
        <h6 className="text-center mt-2">Summary of Performance</h6>
        <hr />
        <Loading />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        dir="ltr"
        className="position-fixed border border-danger-subtle border-3 pt-2 rounded bg-white shadow"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90vh",
          width: "min(960px, 95vw)",
          overflowY: "auto",
        }}
      >
        <h6 className="text-center mt-2">Summary of Performance</h6>
        <hr />
        <ErrorPage data={error} />
        <hr />
        <button
          type="button"
          onClick={() => close(false)}
          className="btn btn-outline-primary me-4 mb-3 px-5"
        >
          بستن
        </button>
      </div>
    );
  }

  if (isSuccess && data) {
    const isGeneralExam =
      data.exam_type === "GENERAL" ||
      data.exam_type === "GENERAL1" ||
      data.exam_type === "GENERAL2";

    const { scores, sections } = data;

    return (
      <div
        dir="ltr"
        className="position-fixed border border-danger-subtle border-3 pt-2 rounded bg-white shadow"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90vh",
          width: "min(960px, 95vw)",
          overflowY: "auto",
        }}
      >
        <h6 className="text-center mt-2">Summary of Performance</h6>
        <hr />
        <div className="px-4 pb-3">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div style={{ fontSize: "13px", lineHeight: 1.4, textAlign: "left" }}>
              <p className="mb-1">
                Exam type: <b>{data.exam_type}</b>
              </p>
              <p className="mb-0 small text-muted">
                This report summarizes your performance by section.
              </p>
            </div>
          </div>

          {isGeneralExam ? (
            <div className="d-flex flex-wrap mb-4">
              {renderGeneralScoreCard(
                "Verbal Reasoning",
                scores.Verbal?.correct,
                scores.Verbal?.pers
              )}
              {renderGeneralScoreCard(
                "Quantitative Reasoning",
                scores.Quant?.correct,
                scores.Quant?.pers
              )}
            </div>
          ) : (
            <div className="mb-4">
              {data.exam_type === "VERBAL" &&
                renderSingleScoreCard(
                  "Verbal Reasoning",
                  scores.Verbal?.correct,
                  scores.Verbal?.all
                )}
              {data.exam_type === "QUANT" &&
                renderSingleScoreCard(
                  "Quantitative Reasoning",
                  scores.Quant?.correct,
                  scores.Quant?.all
                )}
              {data.exam_type === "WRITING" &&
                renderSingleScoreCard(
                  "Analytical Writing",
                  scores.Writing?.correct,
                  scores.Writing?.all
                )}
            </div>
          )}

          <h6
            className="mb-2"
            style={{ fontSize: "14px", fontWeight: 600, textAlign: "left" }}
          >
            Section Summary
          </h6>

          <div className="table-responsive">
            <table
              className="table table-sm mb-0"
              style={{ borderCollapse: "collapse", fontSize: "12px" }}
            >
              <thead>
                <tr>
                  <th
                    className="text-center"
                    style={{
                      width: "10%",
                      backgroundColor: "#5d1549",
                      color: "#fff",
                      border: "1px solid #000",
                      padding: "4px",
                    }}
                  >
                    Section #
                  </th>
                  <th
                    className="text-center"
                    style={{
                      width: "20%",
                      backgroundColor: "#5d1549",
                      color: "#fff",
                      border: "1px solid #000",
                      padding: "4px",
                    }}
                  >
                    Section Name
                  </th>
                  <th
                    className="text-center"
                    style={{
                      width: "12%",
                      backgroundColor: "#5d1549",
                      color: "#fff",
                      border: "1px solid #000",
                      padding: "4px",
                    }}
                  >
                    Total Questions
                  </th>
                  <th
                    className="text-center"
                    style={{
                      width: "15%",
                      backgroundColor: "#5d1549",
                      color: "#fff",
                      border: "1px solid #000",
                      padding: "4px",
                    }}
                  >
                    Total Questions Answered
                  </th>
                  <th
                    className="text-center"
                    style={{
                      width: "15%",
                      backgroundColor: "#5d1549",
                      color: "#fff",
                      border: "1px solid #000",
                      padding: "4px",
                    }}
                  >
                    Questions Answered Correctly
                  </th>
                  <th
                    className="text-center"
                    style={{
                      width: "15%",
                      backgroundColor: "#5d1549",
                      color: "#fff",
                      border: "1px solid #000",
                      padding: "4px",
                    }}
                  >
                    Questions Answered Incorrectly
                  </th>
                  <th
                    className="text-center"
                    style={{
                      width: "13%",
                      backgroundColor: "#5d1549",
                      color: "#fff",
                      border: "1px solid #000",
                      padding: "4px",
                    }}
                  >
                    Questions Not Answered
                  </th>
                </tr>
              </thead>
              <tbody>
                {sections.map((section) => (
                  <tr key={section.section_number}>
                    <td
                      className="text-center align-middle"
                      style={{
                        border: "1px solid #000",
                        padding: "3px 4px",
                      }}
                    >
                      {section.section_number}
                    </td>
                    <td
                      className="text-center align-middle"
                      style={{
                        border: "1px solid #000",
                        padding: "3px 4px",
                      }}
                    >
                      {section.section_name}
                    </td>
                    <td
                      className="text-center align-middle"
                      style={{
                        border: "1px solid #000",
                        padding: "3px 4px",
                      }}
                    >
                      {section.total_questions}
                    </td>
                    <td
                      className="text-center align-middle"
                      style={{
                        border: "1px solid #000",
                        padding: "3px 4px",
                      }}
                    >
                      {section.answered}
                    </td>
                    <td
                      className="text-center align-middle"
                      style={{
                        border: "1px solid #000",
                        padding: "3px 4px",
                      }}
                    >
                      {section.correct}
                    </td>
                    <td
                      className="text-center align-middle"
                      style={{
                        border: "1px solid #000",
                        padding: "3px 4px",
                      }}
                    >
                      {section.incorrect}
                    </td>
                    <td
                      className="text-center align-middle"
                      style={{
                        border: "1px solid #000",
                        padding: "3px 4px",
                      }}
                    >
                      {section.not_answered}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div
            className="mt-2"
            style={{ fontSize: "11px", textAlign: "left", color: "#555" }}
          >
            * Questions not answered are counted as incorrect in the scoring process.
          </div>
        </div>
        <hr />
        <button
          type="button"
          onClick={() => close(false)}
          className="btn btn-outline-primary me-4 mb-3 px-5"
        >
          بستن
        </button>
      </div>
    );
  }

  return null;
}

export default PerformanceSummary;
